import {
  isTaskToFinishRelationType,
  isTaskToStartRelationType,
  JobContractDaysPeriodType,
  ScheduleTaskType,
  TaskRelationType,
} from '@gh/builder-shared-data';
import {
  createTableDriver,
  getInvalidFields,
  isEntityInvalid,
  onChangeReducer,
  removeEntityFields,
  resetDirtyType,
  setBaseDates,
  setEntityField,
  setEntityFields,
  setNestedEntityField,
  setNewDirtyType,
  Table,
  toOnChangeReducer,
} from '@gh/common-shared';
import { DirtyType, ID, WithDirty } from '@gh/common-shared-data';
import { FocusedCell } from '@gh/core-ui-grid';
import {
  addDays,
  addHours,
  addMonths,
  arrayIterator,
  cloneDate,
  currentNormalizedDate,
  getTime,
  isDateValid,
  isDateWeekend,
  isNotNil,
  maxDate,
  minDate,
  normalizeDate,
  setHours,
  toString,
} from '@gh/core-util';
import {
  clone,
  filter,
  find,
  first,
  flatMap,
  forEachRight,
  fromPairs,
  intersection,
  isEmpty,
  isNil,
  keyBy,
  map,
  mapValues,
  max,
  mean,
  omit,
  padStart,
  pick,
  some,
  stubTrue,
  uniq,
  values,
} from 'lodash';
import {
  calculateCalendarDuration,
  CalendarListItem,
  copyGanttDate,
  GANTT_TASK_ROOT_ID,
  GANTT_WORKING_HOURS,
  GanttTask,
  ganttTaskIdArr,
  ganttTaskIdToParentId,
  GanttTimelineTask,
  getCalendarListItemKey,
  getGanttTaskId,
  getGanttTaskParentId,
  HOURS_START_OF_DAY,
  hoursTillEndOfBusinessDay,
  isGanttStartTask,
  isGanttTaskCompleted,
  isGanttTaskIdForSchedule,
  isGanttTaskIncluded,
  isGanttTaskRootId,
  isGanttTaskTopId,
  isGanttTopTask,
  selectGanttTask,
  setGanttTaskField,
  setGanttTaskFields,
  setGanttTaskFieldsWithoutDirty,
  setGanttTaskHasChildren,
  syncGanttCompletionDate,
  WORK_TIME_END,
  WORK_TIME_HOURS_END,
  WORK_TIME_HOURS_START,
  WORK_TIME_START,
} from './resources/gantt.model';

export type GanttState = WithDirty<{
  editable: boolean;
  tasks: Hash<GanttTask>;
  periodType: JobContractDaysPeriodType;
  calendar: Table<CalendarListItem>;
  tasksByParent: Hash<ID[]>;
  startDate: Date;
  endDate: Date;
  startVisibleDate: Date;
  endVisibleDate: Date;
  // current date (if exist) is always visible
  currentDate?: Date;
  focusedCell?: FocusedCell;
  showCompletedTasks: boolean;
  showMarkedAsDeletedTasks: boolean;
  multiMode: boolean;
  minDate?: Date;
  history?: GanttState[];
  historyPosition: number;
  historyLength: number;
  lastUpdateTime: number;
  treeExpandVersion: number;
  structureVersion: number;
  historyRestoreVersion: number;
}>;

export enum GanttStateEventType {
  Load = 'Load',
  LoadTasks = 'LoadTasks',
  Merge = 'Merge',
  TaskAdd = 'TaskAdd',
  TaskAddSet = 'TaskAddSet',
  TaskRemove = 'TaskRemove',
  TaskSetField = 'TaskSetField',
  EditTaskDialog = 'EditTaskDialog',
  TasksSetExcl = 'TasksSetExcl',
  TaskSetFields = 'TaskSetFields',
  TaskSetFieldsBatch = 'TaskSetFieldsBatch',
  TaskSetFieldsWithoutDirty = 'setTaskFieldsWithoutDirty',
  TaskMove = 'TaskMove',
  TaskUpdateFromTimeline = 'TaskUpdateFromTimeline',
  TaskOpen = 'TaskOpen',
  TaskOpenAll = 'TaskOpenAll',
  NavigateToInvalid = 'NavigateToInvalid',
  ResetFocusedCell = 'ResetFocusedCell',
  ResetDirty = 'ResetDirty',
  SetShowCompletedTasks = 'SetShowCompletedTasks',
  SetShowDeletedTasks = 'SetShowDeletedTasks',
  TaskSyncTopsForMultiMode = 'TaskSyncTopsForMultiMode',
  RecalculateStartEndDate = 'RecalculateStartEndDate',
  SelectTask = 'SelectTask',
  UnselectTask = 'UnselectTask',
  SaveBaseDates = 'SaveBaseDates',
  InitHistory = 'InitHistory',
  AddToHistory = 'AddToHistory',
  Undo = 'Undo',
  Redo = 'Redo',
  MarkAsDeleted = 'MarkAsDeleted',
  UnMarkAsDeleted = 'UnMarkAsDeleted',
}

export type GanttStateLoadEvent = {
  type: GanttStateEventType.Load;
  periodType: JobContractDaysPeriodType;
  tasks: GanttTask[];
  calendar: CalendarListItem[];
  editable: boolean;
  multiMode: boolean;
  minDate: Date;
  currentDate?: Date;
};

export type GanttStateEditTaskDialogEvent = {
  type: GanttStateEventType.EditTaskDialog;
  task: GanttTask;
};

export type GanttStateLoadTasksEvent = {
  type: GanttStateEventType.LoadTasks;
  tasks: GanttTask[];
};
export type GanttStateMergeEvent = {
  type: GanttStateEventType.Merge;
  tasks: GanttTask[];
};
export type GanttStateTaskAddEvent = {
  type: GanttStateEventType.TaskAdd;
  task: GanttTask;
  destination?: ID;
  destinationOrder: number;
};
export type GanttStateTaskAddSetEvent = {
  type: GanttStateEventType.TaskAddSet;
  tasks: GanttTask[];
  destination?: ID;
};
export type GanttStateTaskRemoveEvent = {
  type: GanttStateEventType.TaskRemove;
  taskId: ID;
};
export type GanttStateTaskSetFieldEvent = {
  type: GanttStateEventType.TaskSetField;
  taskId: ID;
  field: keyof GanttTask;
  value: any;
};

export type GanttStateTasksSetExclEvent = {
  type: GanttStateEventType.TasksSetExcl;
  task: GanttTask;
  idList: ID[];
  parentsList: ID[];
  value: boolean;
};

export type GanttStateTaskSetFieldsEvent = {
  type: GanttStateEventType.TaskSetFields;
  taskId: ID;
  fields: Hash<any>;
};
export type GanttStateTaskSetFieldsBatchEvent = {
  type: GanttStateEventType.TaskSetFieldsBatch;
  updates: Array<{ taskId: ID; fields: Hash<any> }>;
};
export type GanttStateTaskSetFieldsWithoutDirtyEvent = {
  type: GanttStateEventType.TaskSetFieldsWithoutDirty;
  taskId: ID;
  fields: Hash<any>;
};
export type GanttStateTaskMoveEvent = {
  type: GanttStateEventType.TaskMove;
  source: ID;
  destination?: ID;
  destinationOrder: number;
};
export type GanttStateTaskUpdateFromTimelineEvent = {
  type: GanttStateEventType.TaskUpdateFromTimeline;
  tasks: GanttTimelineTask[];
};
export type GanttStateTaskOpenEvent = { type: GanttStateEventType.TaskOpen; taskId: ID; opened: boolean };
export type GanttStateTaskOpenAllEvent = {
  type: GanttStateEventType.TaskOpenAll;
  taskIds: ID[];
  opened: boolean;
};
export type GanttStateTaskSelectEvent = {
  type: GanttStateEventType.SelectTask;
  taskId: ID;
};
export type GanttStateTaskUnselectEvent = {
  type: GanttStateEventType.UnselectTask;
  taskId: ID;
};

export type GanttStateUndoEvent = {
  type: GanttStateEventType.Undo;
};

export type GanttStateRedoEvent = {
  type: GanttStateEventType.Redo;
};

export type GanttStateInitHistoryEvent = {
  type: GanttStateEventType.InitHistory;
};

export type GanttStateAddToHistoryEvent = {
  type: GanttStateEventType.AddToHistory;
};

export type GanttStateMarkAsDeletedEvent = {
  type: GanttStateEventType.MarkAsDeleted;
  taskId: ID;
};

export type GanttStateUnMarkAsDeletedEvent = {
  type: GanttStateEventType.UnMarkAsDeleted;
  taskId: ID;
};

export type GanttStateNavigateToInvalidEvent = { type: GanttStateEventType.NavigateToInvalid };
export type GanttStateResetDirtyEvent = { type: GanttStateEventType.ResetDirty; isDirty: boolean };
export type GanttStateResetFocusedCellEvent = { type: GanttStateEventType.ResetFocusedCell };
export type GanttStateSetShowCompletedTasksEvent = { type: GanttStateEventType.SetShowCompletedTasks; show: boolean };
export type GanttStateSetShowDeletedTasksEvent = { type: GanttStateEventType.SetShowDeletedTasks; show: boolean };
export type GanttStateTaskSyncTopsForMultiModeEvent = { type: GanttStateEventType.TaskSyncTopsForMultiMode };
export type GanttStateSaveBaseDatesEvent = { type: GanttStateEventType.SaveBaseDates };
export type GanttStateRecalculateStartEndDateEvent = {
  type: GanttStateEventType.RecalculateStartEndDate;
  startDate: Date;
  endDate: Date;
};

export type GanttStateEvent = GanttStateLoadEvent | GanttStateMergeEvent
  | GanttStateTaskOpenEvent | GanttStateTaskOpenAllEvent | GanttStateLoadTasksEvent
  | GanttStateTaskAddEvent | GanttStateTaskAddSetEvent | GanttStateTasksSetExclEvent
  | GanttStateTaskRemoveEvent | GanttStateTaskSetFieldEvent | GanttStateTaskSetFieldsWithoutDirtyEvent
  | GanttStateTaskSetFieldsEvent | GanttStateTaskSetFieldsBatchEvent | GanttStateTaskMoveEvent
  | GanttStateTaskUpdateFromTimelineEvent
  | GanttStateNavigateToInvalidEvent | GanttStateResetFocusedCellEvent | GanttStateResetDirtyEvent
  | GanttStateSetShowCompletedTasksEvent | GanttStateEditTaskDialogEvent
  | GanttStateTaskSyncTopsForMultiModeEvent | GanttStateRecalculateStartEndDateEvent
  | GanttStateTaskSelectEvent | GanttStateTaskUnselectEvent | GanttStateSaveBaseDatesEvent
  | GanttStateUndoEvent | GanttStateRedoEvent | GanttStateInitHistoryEvent | GanttStateAddToHistoryEvent
  | GanttStateMarkAsDeletedEvent | GanttStateUnMarkAsDeletedEvent
  | GanttStateSetShowDeletedTasksEvent;

export const calendarTable = createTableDriver(getCalendarListItemKey);

export const GANTT_INIT_STATE: GanttState = {
  editable: false,
  tasks: {},
  periodType: JobContractDaysPeriodType.AllDays,
  calendar: calendarTable.create(),
  tasksByParent: {},
  startDate: currentNormalizedDate(),
  endDate: currentNormalizedDate(),
  startVisibleDate: currentNormalizedDate(),
  endVisibleDate: currentNormalizedDate(),
  isDirty: false,
  showCompletedTasks: true,
  showMarkedAsDeletedTasks: false,
  multiMode: false,
  minDate: void 0,
  history: [],
  historyPosition: 0,
  historyLength: 13,
  lastUpdateTime: new Date().getTime(),
  treeExpandVersion: 0,
  structureVersion: 0,
  historyRestoreVersion: 0,
};

export const ganttReducer = onChangeReducer(mainReducer, toOnChangeReducer(dirtyFlagReducer));

function bumpTreeExpandVersion(state: GanttState): GanttState {
  return setEntityField(state, 'treeExpandVersion', (state.treeExpandVersion ?? 0) + 1);
}

function bumpStructureVersion(state: GanttState): GanttState {
  return setEntityField(state, 'structureVersion', (state.structureVersion ?? 0) + 1);
}

function bumpHistoryRestoreVersion(state: GanttState): GanttState {
  return setEntityField(state, 'historyRestoreVersion', (state.historyRestoreVersion ?? 0) + 1);
}

function getAllTasksInTreeOrder(state: GanttState): GanttTask[] {
  const { tasks, tasksByParent } = state;
  const stack: ID[] = [];
  const allTasks: GanttTask[] = [];
  forEachRight(tasksByParent[GANTT_TASK_ROOT_ID], (taskId) => stack.push(taskId));
  while (stack.length) {
    const taskId = stack.pop()!;
    const task = tasks[taskId];
    if (task) {
      allTasks.push(task);
    }
    forEachRight(tasksByParent[taskId], (childTaskId) => stack.push(childTaskId));
  }
  return allTasks;
}

function renumberTasksInState(state: GanttState): GanttState {
  const orderedTasks = getAllTasksInTreeOrder(state);
  const tasks = { ...state.tasks };
  orderedTasks.forEach((task, index) => {
    tasks[task.taskId] = { ...tasks[task.taskId], taskNumber: index + 1 };
  });
  orderedTasks.forEach((task) => {
    const current = tasks[task.taskId];
    if (!current?.predecessors?.length) {
      return;
    }
    tasks[task.taskId] = {
      ...current,
      predecessors: current.predecessors.map((predecessor) => ({
        ...predecessor,
        taskNo: tasks[predecessor.taskId]?.taskNumber ?? predecessor.taskNo,
      })),
    };
  });
  return setEntityField(state, 'tasks', tasks);
}

function mainReducer(state: GanttState, event: GanttStateEvent): GanttState {
  switch (event.type) {
    case GanttStateEventType.Load:
      return bumpStructureVersion(bumpTreeExpandVersion(loadTasks(state, event)));
    case GanttStateEventType.EditTaskDialog:
      return editTaskDialog(state, event.task);
    case GanttStateEventType.LoadTasks:
      return bumpStructureVersion(bumpTreeExpandVersion(loadTasksOnly(state, event)));
    case GanttStateEventType.Merge:
      return bumpStructureVersion(mergeTasks(state, event.tasks));
    case GanttStateEventType.TaskMove:
      return bumpStructureVersion(
        renumberTasksInState(moveTask(state, event.source, event.destination, event.destinationOrder)),
      );
    case GanttStateEventType.TaskAdd:
      return bumpStructureVersion(recalculateVisibleRange(
        renumberTasksInState(addTask(state, event.task, event.destination, event.destinationOrder)),
      ));
    case GanttStateEventType.TaskAddSet:
      return bumpStructureVersion(recalculateVisibleRange(
        renumberTasksInState(addTaskSet(state, event.tasks, event.destination)),
      ));
    case GanttStateEventType.TaskSetFields:
      return bumpStructureVersionIfCompletionVisibilityChanged(
        state,
        setTaskFields(state, event.taskId, event.fields),
        [event.taskId],
      );
    case GanttStateEventType.TaskSetFieldsBatch:
      return bumpStructureVersionIfCompletionVisibilityChanged(
        state,
        setTaskFieldsBatch(state, event.updates),
        event.updates.map(({ taskId }) => taskId),
      );
    case GanttStateEventType.TaskSetFieldsWithoutDirty:
      return setTaskFieldsWithoutDirty(state, event.taskId, event.fields);
    case GanttStateEventType.TaskSetField: {
      const nextState = setTaskField(state, event.taskId, event.field, event.value);
      return event.field === 'completionPct'
        ? bumpStructureVersionIfCompletionVisibilityChanged(state, nextState, [event.taskId])
        : nextState;
    }
    case GanttStateEventType.TasksSetExcl:
      return recalculateVisibleRange(
        recalculateVisibleRangeTasks(state, event.task, event.idList, event.parentsList, event.value),
      );
    case GanttStateEventType.TaskRemove:
      return bumpStructureVersion(renumberTasksInState(removeTask(state, event.taskId)));
    case GanttStateEventType.TaskUpdateFromTimeline:
      return recalculateVisibleRange(updateTasksFromTimeline(state, event.tasks));
    case GanttStateEventType.TaskOpen:
      return bumpTreeExpandVersion(openTask(state, event.taskId, event.opened));
    case GanttStateEventType.TaskOpenAll:
      return bumpTreeExpandVersion(openAllTasks(state, event.taskIds, event.opened));
    case GanttStateEventType.ResetFocusedCell:
      return navigateToCell(state);
    case GanttStateEventType.NavigateToInvalid:
      return navigateToInvalid(state);
    case GanttStateEventType.ResetDirty:
      return resetDirty(state);
    case GanttStateEventType.SetShowCompletedTasks:
      return setShowCompletedTasks(state, event.show);
    case GanttStateEventType.SetShowDeletedTasks:
      return setShowDeletedTasks(state, event.show);
    case GanttStateEventType.TaskSyncTopsForMultiMode:
      return syncTopTasksForMultiMode(state);
    case GanttStateEventType.RecalculateStartEndDate:
      return recalculateStartEndDate(state, event.startDate, event.endDate);
    case GanttStateEventType.SelectTask:
      return selectTask(state, event.taskId, true);
    case GanttStateEventType.UnselectTask:
      return selectTask(state, event.taskId, false);
    case GanttStateEventType.SaveBaseDates:
      return saveBaseDates(state);
    case GanttStateEventType.Undo:
      return bumpHistoryRestoreVersion(bumpStructureVersion(bumpTreeExpandVersion(renumberTasksInState(undo(state)))));
    case GanttStateEventType.Redo:
      return bumpHistoryRestoreVersion(bumpStructureVersion(bumpTreeExpandVersion(renumberTasksInState(redo(state)))));
    case GanttStateEventType.InitHistory:
      return initHistory(state);
    case GanttStateEventType.AddToHistory:
      return addToHistory(state);
    case GanttStateEventType.MarkAsDeleted:
      return bumpStructureVersion(renumberTasksInState(markAsDeleteTask(state, event.taskId)));
    case GanttStateEventType.UnMarkAsDeleted:
      return bumpStructureVersion(setTaskField(state, event.taskId, 'markedAsDeleted', false));
    default:
      return state;
  }
}

function dirtyFlagReducer(state: GanttState, event: GanttStateEvent): GanttState {
  switch (event.type) {
    case GanttStateEventType.TaskMove:
    case GanttStateEventType.EditTaskDialog:
    case GanttStateEventType.TaskAdd:
    case GanttStateEventType.TasksSetExcl:
    case GanttStateEventType.TaskAddSet:
    case GanttStateEventType.TaskSetField:
    case GanttStateEventType.TaskSetFields:
    case GanttStateEventType.TaskSetFieldsBatch:
    case GanttStateEventType.TaskRemove:
    case GanttStateEventType.TaskUpdateFromTimeline:
    case GanttStateEventType.MarkAsDeleted:
    case GanttStateEventType.UnMarkAsDeleted:
      return setEntityFields(state, {
        isDirty: true,
        lastUpdateTime: new Date().getTime(),
      });
    default:
      return state;
  }
}

function loadTasksOnly(state: GanttState, event: GanttStateLoadTasksEvent): GanttState {
  const { tasks } = event;
  const tasksById = keyBy(tasks, getGanttTaskId);
  const tasksByParent = {};
  tasks.forEach((task) => {
    const taskId = getGanttTaskId(task);
    const parentTaskId = getGanttTaskParentId(task);
    let children = tasksByParent[parentTaskId];
    if (!children) {
      children = tasksByParent[parentTaskId] = [];
    }
    children.push(taskId);
  });
  return recalculateVisibleRange(setEntityFields(state, {
    tasks: tasksById,
  }));
}

function loadTasks(state: GanttState, event: GanttStateLoadEvent): GanttState {
  const { tasks, periodType, calendar = [], editable, multiMode, minDate, currentDate } = event;
  const tasksById = keyBy(tasks, getGanttTaskId);
  const tasksByParent = {};
  tasks.forEach((task) => {
    const taskId = getGanttTaskId(task);
    const parentTaskId = getGanttTaskParentId(task);
    let children = tasksByParent[parentTaskId];
    if (!children) {
      children = tasksByParent[parentTaskId] = [];
    }
    children.push(taskId);
  });
  return recalculateVisibleRange(setEntityFields(state, {
    editable,
    tasks: tasksById,
    periodType,
    calendar: calendarTable.create(arrayIterator(calendar)),
    tasksByParent,
    multiMode,
    minDate,
    currentDate,
  }));
}

function editTaskDialog(state: GanttState, task: GanttTask): GanttState {
  return setEntityFields(state, {
    tasks: {
      ...state.tasks,
      [task.taskId]: {
        ...state.tasks[task.taskId],
        ...task,
        dirtyType: DirtyType.Updated,
      },
    },
    isDirty: true,
  });
}

function mergeTasks(
  state: GanttState,
  tasks: GanttTask[],
): GanttState {
  // currently we merge only physicalTaskId(s) to actualize ID after saving
  const tasksById = mapValues(
    keyBy(tasks, getGanttTaskId),
    (task) => setEntityFields(
      state.tasks[getGanttTaskId(task)],
      pick(task, ['physicalTaskId', 'baseEndDate', 'baseStartDate', 'isResourceInUse', 'canBeMarkedAsDeleted'],
      )),
  );
  const tasksByParent = {};
  tasks.forEach((task) => {
    const taskId = getGanttTaskId(task);
    const parentTaskId = getGanttTaskParentId(task);
    let children = tasksByParent[parentTaskId];
    if (!children) {
      children = tasksByParent[parentTaskId] = [];
    }
    children.push(taskId);
  });
  return setEntityFields(state, {
    tasks: tasksById,
    tasksByParent,
  });
}

function addTask(state: GanttState, task: GanttTask, destination: Maybe<ID>, destinationOrder: number): GanttState {
  const taskId = getGanttTaskId(task);
  const destinationTaskId = ganttTaskIdToParentId(destination);
  return setEntityFields(state, {
    tasks: setEntityFields(state.tasks, {
      [taskId]: setNewDirtyType(task),
      ...(isGanttTaskRootId(destinationTaskId)
        ? {} :
        {
          [destinationTaskId]: setEntityField(
            setGanttTaskHasChildren(state.tasks[destinationTaskId], true),
            'opened',
            true,
          ),
        }),
    }),
    tasksByParent: setEntityFields(state.tasksByParent, {
      [destinationTaskId]: ganttTaskIdArr.insert(
        state.tasksByParent[destinationTaskId] || [],
        destinationOrder,
        taskId,
      ),
    }),
  });
}

function addTaskSet(state: GanttState, tasks: GanttTask[], destination: Maybe<ID>): GanttState {
  const taskIds = tasks.map(getGanttTaskId);
  const destinationTaskId = ganttTaskIdToParentId(destination);
  return setEntityFields(state, {
    tasks: {
      ...state.tasks,
      ...fromPairs(tasks.map(setNewDirtyType).map((task) => [getGanttTaskId(task), task])),
      ...(isGanttTaskRootId(destinationTaskId)
        ? {} :
        {
          [destinationTaskId]: setGanttTaskHasChildren(state.tasks[destinationTaskId], true),
        }),
    },
    tasksByParent: setEntityFields(state.tasksByParent, {
      [destinationTaskId]: ganttTaskIdArr.append(
        state.tasksByParent[destinationTaskId] || [],
        ...taskIds,
      ),
    }),
  });
}

function selectTask(state: GanttState, taskId: ID, selected: boolean): GanttState {
  return setNestedEntityField(
    state,
    ['tasks', taskId],
    selectGanttTask(state.tasks[taskId], 'selected', selected),
  );
}

function setTaskField(state: GanttState, taskId: ID, field: keyof GanttTask, value: any): GanttState {
  return setNestedEntityField(
    state,
    ['tasks', taskId],
    syncGanttCompletionDate(setGanttTaskField(state.tasks[taskId], field, value)),
  );
}

function setTaskFieldsWithoutDirty(state: GanttState, taskId: ID, fields: Hash<any>): GanttState {
  return setNestedEntityField(
    state,
    ['tasks', String(taskId)],
    syncGanttCompletionDate(setGanttTaskFieldsWithoutDirty(state.tasks[taskId], fields)),
  );
}

export function setTaskFields(state: GanttState, taskId: ID, fields: Hash<any>): GanttState {
  return setNestedEntityField(
    state,
    ['tasks', String(taskId)],
    syncGanttCompletionDate(setGanttTaskFields(state.tasks[taskId], fields)),
  );
}

function setTaskFieldsBatch(
  state: GanttState,
  updates: Array<{ taskId: ID; fields: Hash<any> }>,
): GanttState {
  return updates.reduce(
    (nextState, { taskId, fields }) => setTaskFields(nextState, taskId, fields),
    state,
  );
}

function bumpStructureVersionIfCompletionVisibilityChanged(
  state: GanttState,
  nextState: GanttState,
  taskIds: ID[],
): GanttState {
  if (state.showCompletedTasks || nextState === state) {
    return nextState;
  }
  const visibilityChanged = taskIds.some((taskId) => {
    const before = state.tasks[taskId];
    const after = nextState.tasks[taskId];
    return before && after
      && isGanttTaskCompleted(before) !== isGanttTaskCompleted(after);
  });
  return visibilityChanged ? bumpStructureVersion(nextState) : nextState;
}

function markAsDeleteTask(state: GanttState, taskId: ID): GanttState {
  const task = state.tasks[taskId];
  if (!task) {
    return state;
  }

  let tasks: Hash<GanttTask> = {
    ...state.tasks,
    [taskId]: {
      ...task,
      markedAsDeleted: true,
      predecessors: [],
      ...(task.parentTaskId ? { parentTaskId: '' } : {}),
      dirtyType: DirtyType.Updated,
    },
  };

  const relationsTask = queryGanttAllTasks(state)
    .filter((item) => item.predecessors?.length
      && item.predecessors?.find((predecessor) => predecessor.taskId === taskId));
  relationsTask.forEach((relatedTask) => {
    const relatedTaskId = relatedTask.taskId;
    const current = tasks[relatedTaskId] ?? relatedTask;
    tasks = {
      ...tasks,
      [relatedTaskId]: {
        ...current,
        predecessors: current.predecessors?.filter((predecessor) => predecessor.taskId !== taskId),
      },
    };
  });

  return setEntityFields(state, {
    tasks,
  });
}

function removeTask(state: GanttState, taskId: ID): GanttState {
  const taskToBeRemoved = state.tasks[taskId];
  const parentTaskId = ganttTaskIdToParentId(taskToBeRemoved.parentTaskId);
  const removedTaskIds = queryGanttSubtreeTaskIds(state, taskId);
  const removedTaskIdIndex = keyBy(removedTaskIds);

  // map of tasks without removed
  const tasksWithoutRemoved: Hash<GanttTask> = removeEntityFields(state.tasks, <any>removedTaskIds);
  // set if parent task still has children
  const newParentChildren = ganttTaskIdArr.remove(state.tasksByParent[parentTaskId], taskId);
  const tasksWithUpdatedParent: Hash<GanttTask> = {
    ...tasksWithoutRemoved,
    ...(isGanttTaskRootId(parentTaskId)
      ? {} :
      {
        [parentTaskId]: setGanttTaskHasChildren(
          state.tasks[parentTaskId],
          newParentChildren.filter((childId) => isGanttTaskIncluded(state.tasks[childId])).length > 0,
        ),
      }),
  };
  // clean all references to removed tasks
  const tasksWithCleanedPredecessors = mapValues(tasksWithUpdatedParent, (task) => {
    if (some(task.predecessors, (predecessor) => removedTaskIdIndex[predecessor.taskId])) {
      return setGanttTaskFields(
        task,
        { 'predecessors': filter(task.predecessors, (predecessor) => !removedTaskIdIndex[predecessor.taskId]) },
      );
    } else {
      return task;
    }
  });
  return setEntityFields(state, {
    tasks: tasksWithCleanedPredecessors,
    tasksByParent: setEntityFields(removeEntityFields(state.tasksByParent, <any>removedTaskIds), {
      [parentTaskId]: newParentChildren,
    }),
  });
}

function updateTasksFromTimeline(state: GanttState, timelineTasks: GanttTimelineTask[]): GanttState {
  const { tasks } = state;
  return setEntityFields(state, {
    tasks: setEntityFields(tasks, fromPairs(timelineTasks.map((task) => [task.id, setGanttTaskFields(tasks[task.id], {
      // Copies: DHTMLX keeps changing its own Date objects in place (see copyGanttDate)
      startDate: copyGanttDate(task.start_date),
      endDate: copyGanttDate(task.end_date),
      duration: task.duration,
    })]))),
  });
}

function moveTask(state: GanttState, source: ID, destination: Maybe<ID>, destinationOrder: number): GanttState {
  const { tasks, tasksByParent } = state;
  const sourceTask = tasks[source];
  const currentParentTaskId = getGanttTaskParentId(sourceTask);
  const destinationTaskId = ganttTaskIdToParentId(destination);
  if (currentParentTaskId === destinationTaskId) {
    const currentParentChildren = tasksByParent[destinationTaskId];
    return setEntityField(state, 'tasksByParent', setEntityFields(state.tasksByParent, {
      [destinationTaskId]: ganttTaskIdArr.move(
        currentParentChildren,
        ganttTaskIdArr.indexOf(currentParentChildren, source),
        destinationOrder,
      ),
    }));
  } else {
    const currentParentChildren = ganttTaskIdArr.remove(tasksByParent[currentParentTaskId], source);
    const destinationChildren = ganttTaskIdArr.insert(
      tasksByParent[destinationTaskId] || [],
      destinationOrder,
      source,
    );
    return setEntityFields(state, {
      tasks: setEntityFields(tasks, {
        [source]: setEntityField(sourceTask, 'parentTaskId', destination),
        ...(isGanttTaskRootId(currentParentTaskId)
          ? {}
          : {
            [currentParentTaskId]: setGanttTaskHasChildren(
              tasks[currentParentTaskId],
              currentParentChildren.filter((childId) => isGanttTaskIncluded(state.tasks[childId])).length > 0,
            ),
          }),
        ...(isGanttTaskRootId(destinationTaskId)
          ? {}
          : {
            [destinationTaskId]: setGanttTaskHasChildren(
              tasks[destinationTaskId],
              destinationChildren.filter((childId) => isGanttTaskIncluded(state.tasks[childId])).length > 0,
            ),
          }),
      }),
      tasksByParent: setEntityFields(state.tasksByParent, {
        [destinationTaskId]: destinationChildren,
        [currentParentTaskId]: currentParentChildren,
      }),
    });
  }
}

function openTask(state: GanttState, taskId: ID, opened: boolean): GanttState {
  return setNestedEntityField(state, ['tasks', taskId, 'opened'], opened);
}

function openAllTasks(state: GanttState, taskIds: ID[], opened: boolean): GanttState {
  const { tasks } = state;
  return setEntityField(
    state,
    'tasks',
    {
      ...tasks,
      ...fromPairs(taskIds.map((taskId) => [taskId, setEntityField(tasks[taskId], 'opened', opened)])),
    },
  );
}

function navigateToCell(state: GanttState, focusedCell?: FocusedCell): GanttState {
  return setEntityField(state, 'focusedCell', focusedCell);
}

function navigateToInvalid(state: GanttState): GanttState {
  const invalidTask = queryGanttFirstInvalidTask(state);
  const invalidFields = invalidTask ? getInvalidFields(invalidTask) : void 0;
  return isEmpty(invalidFields)
    ? state
    : navigateToCell(state, { id: getGanttTaskId(invalidTask!), field: first(invalidFields)! });
}

function resetDirty(state: GanttState): GanttState {
  return setEntityFields(state, {
    isDirty: false,
    tasks: mapValues(state.tasks, resetDirtyType),
  });
}

function saveBaseDates(state: GanttState): GanttState {
  return setEntityFields(state, {
    tasks: mapValues(state.tasks, setBaseDates),
  });
}

function undo(state: GanttState): GanttState {
  const history = state.history ? [...state.history] : [];
  const historyPosition = state.historyPosition - 1;
  const undoState = history[historyPosition];
  return setEntityFields({ ...undoState, lastUpdateTime: state.lastUpdateTime }, {
    historyPosition,
    history,
    isDirty: undoState.isDirty,
    tasks: getTasksFromHistory(state, undoState),
    tasksByParent: undoState.tasksByParent,
    treeExpandVersion: state.treeExpandVersion,
    structureVersion: state.structureVersion,
    // Keep the live counter: the snapshot holds the value from capture time, so redo could repeat the last value and the
    // timeline (which reloads when this changes) would not reload
    historyRestoreVersion: state.historyRestoreVersion,
  });
}

function redo(state: GanttState): GanttState {
  const history = state.history ? [...state.history] : [];
  const historyPosition = state.historyPosition + 1;
  const redoState = history[historyPosition];
  return setEntityFields({ ...redoState, lastUpdateTime: state.lastUpdateTime }, {
    historyPosition,
    history,
    isDirty: redoState.isDirty,
    tasks: getTasksFromHistory(state, redoState),
    tasksByParent: redoState.tasksByParent,
    treeExpandVersion: state.treeExpandVersion,
    structureVersion: state.structureVersion,
    // Keep the live counter: the snapshot holds the value from capture time, so redo could repeat the last value and the
    // timeline (which reloads when this changes) would not reload
    historyRestoreVersion: state.historyRestoreVersion,
  });
}

function cloneTasksForHistory(tasks: Hash<GanttTask>): Hash<GanttTask> {
  const result: Hash<GanttTask> = {};
  for (const taskId in tasks) {
    const task = tasks[taskId];
    result[taskId] = {
      ...task,
      // Own Date copies so later timeline changes cannot rewrite this snapshot (redo restores it as captured)
      startDate: copyGanttDate(task.startDate),
      endDate: copyGanttDate(task.endDate),
      predecessors: task.predecessors
        ? task.predecessors.map((predecessor) => ({ ...predecessor }))
        : task.predecessors,
      notes: task.notes ? [...task.notes] : task.notes,
    };
  }
  return result;
}

function initHistory(state: GanttState): GanttState {
  const newHistory = {
    ...omit(state, 'history', 'tasks'),
    tasks: cloneTasksForHistory(state.tasks),
  } as GanttState;
  return setEntityFields(state, {
    historyPosition: 0,
    history: [newHistory],
  });
}

function addToHistory(state: GanttState): GanttState {
  const history = state.history ? state.history.slice(0, state.historyPosition + 1) : [];
  const tip = history[history.length - 1];
  if (tip && tip.lastUpdateTime === state.lastUpdateTime && tip.isDirty === state.isDirty) {
    return state;
  }
  const newHistory = {
    ...omit(state, 'history', 'tasks'),
    tasks: cloneTasksForHistory(state.tasks),
  } as GanttState;
  if (history.length < state.historyLength) {
    history.push(newHistory);
  } else {
    history.shift();
    history.push(newHistory);
  }
  const historyPosition = history.length - 1;
  return setEntityFields(state, {
    historyPosition,
    history,
  });
}

function getTasksFromHistory(state: GanttState, history: GanttState): Hash<GanttTask> {
  const result: Hash<GanttTask> = {};
  for (const taskId in history.tasks) {
    const snapshot = history.tasks[taskId];
    const currentTask = state.tasks[taskId];
    result[taskId] = {
      ...snapshot,
      // Restore copies: the snapshot must stay intact for the next undo/redo
      startDate: copyGanttDate(snapshot.startDate),
      endDate: copyGanttDate(snapshot.endDate),
      predecessors: snapshot.predecessors
        ? snapshot.predecessors.map((predecessor) => ({ ...predecessor }))
        : snapshot.predecessors,
      notes: currentTask ? currentTask.notes : [],
      noteMemo: currentTask ? currentTask.noteMemo : '',
      opened: currentTask ? currentTask.opened : snapshot.opened,
      selected: false,
    };
  }
  return result;
}

function recalculateStartEndDate(state: GanttState, startDate: Date, endDate: Date): GanttState {
  return setEntityFields(state, {
    startDate,
    endDate,
  });
}

function syncTopTasksForMultiMode(state: GanttState): GanttState {
  const rootTaskIds = queryGanttRootLevel(state);
  return setEntityField(
    state,
    'tasks',
    setEntityFields(state.tasks, fromPairs(rootTaskIds.map((taskId) => {
      const topTask = queryGanttTask(state, taskId);
      const duration = calculateCalendarDuration(topTask.startDate, topTask.endDate);
      return [taskId, setEntityField(topTask, 'duration', duration)];
    }))),
  );
}

function setShowCompletedTasks(state: GanttState, show: boolean): GanttState {
  return setEntityField(state, 'showCompletedTasks', show);
}

function setShowDeletedTasks(state: GanttState, show: boolean): GanttState {
  return setEntityField(state, 'showMarkedAsDeletedTasks', show);
}

/**
 * Recalculate visible range of dates. Visible range covers all tasks using the following rule:
 * <code>[minTaskStartDate - 1 day, maxTaskEndDate + 1 month]</code>
 */

function minValidTaskStartDate(tasks: GanttTask[]): Date {
  const dates = tasks.map((t) => t.startDate).filter((d): d is Date => isNotNil(d) && isDateValid(d));
  if (!dates.length) {
    return currentNormalizedDate();
  }
  return dates.reduce((min, d) => (d < min ? d : min));
}

function maxValidTaskEndDate(tasks: GanttTask[]): Date {
  const dates = tasks.map((t) => t.endDate).filter((d): d is Date => isNotNil(d) && isDateValid(d));
  if (!dates.length) {
    return currentNormalizedDate();
  }
  return dates.reduce((max, d) => (d > max ? d : max));
}

function recalculateVisibleRangeTasks(state: GanttState, task: GanttTask, idList: ID[], parentsList: ID[], value: boolean): GanttState {
  const tasksById = clone(state.tasks);
  const tasksByParent = state.tasksByParent;
  idList.forEach((id) => {
    if (tasksById[id].isExcludeFromGanttChart !== value) {
      tasksById[id].dirtyType = tasksById[id].dirtyType ? tasksById[id].dirtyType : DirtyType.Updated;
    }
    tasksById[id].isExcludeFromGanttChart = value;
  });
  if (task.type === ScheduleTaskType.Summary || task.type === ScheduleTaskType.CriticalPath) {
    tasksById[task.taskId].mayHaveResource = value;
  }
  if (!value) {
    parentsList.forEach((id) => {
      tasksById[id].isExcludeFromGanttChart = false;
      tasksById[id].mayHaveResource = false;
      tasksById[id].dirtyType = tasksById[id].dirtyType ? tasksById[id].dirtyType : DirtyType.Updated;
    });
  } else {
    if (parentsList && parentsList.length) {
      parentsList.forEach((parentId) => {
        tasksById[parentId].mayHaveResource = true;
        const notExcludedChild = tasksByParent[parentId].find((childId) => !tasksById[childId].isExcludeFromGanttChart);
        if (notExcludedChild) {
          tasksById[parentId].mayHaveResource = false;
        }
      });
    }
  }

  const includedTasks = values(tasksById).filter(isGanttTaskIncluded);
  const earliestDate = minValidTaskStartDate(includedTasks);
  const latestDate = maxValidTaskEndDate(includedTasks);
  const earliestOrCurrentDate = state.currentDate && minDate(state.currentDate, earliestDate) || earliestDate;
  const latestOrCurrentDate = state.currentDate && maxDate(state.currentDate, latestDate) || latestDate;

  let nextTasks = tasksById;
  if (value) {
    const removedTaskIds = queryGanttSubtreeTaskIds(state, task.taskId);
    const removedTaskIdIndex = keyBy(removedTaskIds);
    nextTasks = mapValues(tasksById, (nextTask) => {
      if (some(nextTask.predecessors, (predecessor) => removedTaskIdIndex[predecessor.taskId])) {
        return setGanttTaskFields(
          nextTask,
          { 'predecessors': filter(nextTask.predecessors, (predecessor) => !removedTaskIdIndex[predecessor.taskId]) },
        );
      }
      return nextTask;
    });
  }

  return setEntityFields(state, {
    tasks: nextTasks,
    startDate: earliestOrCurrentDate,
    endDate: latestOrCurrentDate,
    startVisibleDate: addDays(earliestOrCurrentDate, -1),
    endVisibleDate: addMonths(latestOrCurrentDate, 1),
  });
}

function recalculateVisibleRange(state: GanttState): GanttState {
  const includedTasks = values(state.tasks).filter(isGanttTaskIncluded);
  const earliestDate = minValidTaskStartDate(includedTasks);
  const latestDate = maxValidTaskEndDate(includedTasks);
  const earliestOrCurrentDate = state.currentDate && minDate(state.currentDate, earliestDate) || earliestDate;
  const latestOrCurrentDate = state.currentDate && maxDate(state.currentDate, latestDate) || latestDate;
  const startVisibleDate = addDays(earliestOrCurrentDate, -1);
  const endVisibleDate = addMonths(latestOrCurrentDate, 1);

  if (state.startDate?.getTime() === earliestOrCurrentDate.getTime()
    && state.endDate?.getTime() === latestOrCurrentDate.getTime()
    && state.startVisibleDate?.getTime() === startVisibleDate.getTime()
    && state.endVisibleDate?.getTime() === endVisibleDate.getTime()) {
    return state;
  }

  return setEntityFields(state, {
    startDate: earliestOrCurrentDate,
    endDate: latestOrCurrentDate,
    startVisibleDate,
    endVisibleDate,
  });
}

export const queryGanttEditable = ({ editable }: GanttState) => editable;
export const queryGanttFocusedCell = ({ focusedCell }: GanttState) => focusedCell;
export const queryGanttTasks = (state: GanttState) => state.tasks;
export const queryGanttHistory = (state: GanttState) => state.history ? state.history[state.historyPosition] : undefined;
export const queryGanttHistoryPosition = (state: GanttState) => state.historyPosition;
export const queryGanttUndoDisabled = (state: GanttState) => !state.history || !state.history.length ||
  state.history.length === 1 ||
  state.historyPosition === 0;
export const queryGanttRedoDisabled = (state: GanttState) => !state.history || !state.history.length ||
  state.historyPosition === state.history.length - 1;
export const queryGanttLastUpdateTime = (state: GanttState) => state.lastUpdateTime;
export const queryGanttTreeExpandVersion = (state: GanttState) => state.treeExpandVersion;
export const queryGanttHistoryRestoreVersion = (state: GanttState) => state.historyRestoreVersion ?? 0;
export const queryGanttUnconfirmedTaskCount = (state: GanttState): number => {
  let count = 0;
  for (const taskId in state.tasks) {
    if (state.tasks[taskId]?.status === 0) {
      count += 1;
    }
  }
  return count;
};
export const queryGanttStructureVersion = (state: GanttState) => state.structureVersion;
export const queryGanttStartDate = ({ startDate }: GanttState) => startDate;
export const queryGanttEndDate = (state: GanttState) => {
  let endDate = new Date(0);
  const tasks = queryGanttAllTasks(state);
  tasks.forEach((task) => {
    if (task && !task.isExcludeFromGanttChart && task.endDate > endDate) {
      endDate = cloneDate(task.endDate);
    }
  });
  return endDate;
};
export const queryGanttStartVisibleDate = ({ startVisibleDate }: GanttState) => startVisibleDate;
export const queryGanttEndVisibleDate = ({ endVisibleDate }: GanttState) => endVisibleDate;
export const queryGanttChildLevel = (state: GanttState, parentTaskId: ID) =>
  state.tasksByParent[parentTaskId];
export const queryGanttRootLevel = (state: GanttState) => state.tasksByParent[GANTT_TASK_ROOT_ID];
export const queryGanttPeriodType = (state: GanttState) => state.periodType;
export const queryGanttIsAllDaysPeriodType = (state: GanttState) =>
  queryGanttPeriodType(state) === JobContractDaysPeriodType.AllDays;
export const queryGanttCalendar = (state: GanttState) => calendarTable.all(state.calendar);
export const queryGanttCalendarByDate = (state: GanttState, date: Date) =>
  calendarTable.byId(state.calendar, normalizeDate(date).getTime().toString());
export const queryGanttIsDateAllowed = (state: GanttState, date: Date) => {
  if (!date) {
    return false;
  }
  return state.periodType === JobContractDaysPeriodType.AllDays
    || (!isDateWeekend(date) && isNil(queryGanttCalendarByDate(state, date)));
};

export const queryGanttIsDateValid = (state: GanttState, date: Date) => isNotNil(date) && isDateValid(date);
export const queryGanttLeftTrimToWorkTime = (state: GanttState, date: Date) => {
  const time = getTime(date);
  if (time >= WORK_TIME_END || !queryGanttIsDateAllowed(state, date)) {
    return queryGanttTrimByShiftToWorkTime(state, setHours(date, WORK_TIME_HOURS_START), 1);
  } else if (time < WORK_TIME_START) {
    return setHours(date, WORK_TIME_HOURS_START);
  } else {
    return date;
  }
};
/**
 * Trim given date to predecessors of the specified task
 * @param state
 * @param trimmedTaskId
 * @param date
 */
export const queryGanttLeftTrimToPredecessors = (state: GanttState, trimmedTaskId: ID, date: Date) => {
  const task = queryGanttTask(state, trimmedTaskId);
  const toStartPredecessors = filter(task.predecessors, ({ relation }) => isTaskToStartRelationType(relation));
  return max(toStartPredecessors
    .map(({ lag, taskId, relation }) => {
      const predecessorTask = queryGanttTask(state, taskId);
      return relation === TaskRelationType.StartToStart
        ? queryGanttDateAddDuration(state, predecessorTask.startDate, lag)
        : queryGanttDateAddDuration(state, predecessorTask.endDate, lag);
    })
    .concat([date]));
};
export const queryGanttDateAddDuration = (state: GanttState, date: Date, duration: number) => {
  const tillEndOfDay = Math.min(hoursTillEndOfBusinessDay(date), duration);
  let restDuration = duration - tillEndOfDay;
  let currentDate = date;
  while (restDuration > 0) {
    currentDate = setHours(addDays(currentDate, 1), HOURS_START_OF_DAY);
    if (queryGanttIsDateAllowed(state, currentDate)) {
      const spentHours = Math.min(GANTT_WORKING_HOURS, restDuration);
      restDuration = restDuration - spentHours;
      currentDate = addHours(currentDate, spentHours);
    }
  }
  return currentDate;
};
export const queryGanttRightTrimToWorkTime = (state: GanttState, date: Date) => {
  const time = getTime(date);
  if (time <= WORK_TIME_START || !queryGanttIsDateAllowed(state, date)) {
    return queryGanttTrimByShiftToWorkTime(state, setHours(date, WORK_TIME_HOURS_END), -1);
  } else if (time > WORK_TIME_END) {
    return setHours(date, WORK_TIME_HOURS_END);
  } else {
    return date;
  }
};
/**
 * Trim given date from the right to predecessors of the given task
 * @param state
 * @param trimmedTaskId
 * @param date
 */
export const queryGanttRightTrimToPredecessors = (state: GanttState, trimmedTaskId: ID, date: Date) => {
  const task = queryGanttTask(state, trimmedTaskId);
  const toFinishPredecessors = filter(task.predecessors, ({ relation }) => isTaskToFinishRelationType(relation));
  return max(toFinishPredecessors
    .map(({ lag, taskId, relation }) => {
      const predecessorTask = queryGanttTask(state, taskId);
      return relation === TaskRelationType.FinishToFinish
        ? addDays(predecessorTask.endDate, lag)
        : addDays(predecessorTask.startDate, lag);
    })
    .concat([date]));
};
export const queryGanttTrimByShiftToWorkTime = (state: GanttState, date: Date, shift: number) => {
  let nextDate: Date = date;
  do {
    nextDate = addDays(nextDate, shift);
  } while (!queryGanttIsDateAllowed(state, nextDate));
  return nextDate;
};
export const queryGanttTasksByParent = (state: GanttState) => state.tasksByParent;
export const queryGanttShowCompletedTasks = (state: GanttState) => state.showCompletedTasks;
export const queryGanttShowMarkedAsDeletedTasks = (state: GanttState) => state.showMarkedAsDeletedTasks;
const queryGanttTasksByPredicate = (
  { tasks, tasksByParent }: GanttState,
  predicate: PredicateFn<GanttTask>,
): GanttTask[] => {
  const stack: ID[] = [];
  const allTasks: GanttTask[] = [];
  forEachRight(tasksByParent[GANTT_TASK_ROOT_ID], (taskId) => stack.push(taskId));
  while (stack.length) {
    const taskId = stack.pop()!;
    const task = tasks[taskId];
    if (task && predicate(task)) {
      allTasks.push(task);
    }

    forEachRight(tasksByParent[taskId], (childTaskId) => stack.push(childTaskId));
  }
  return allTasks;
};
export const queryGanttVisibleTaskSeq = (state: GanttState) => {
  const { showCompletedTasks, showMarkedAsDeletedTasks } = state;
  return queryGanttTasksByPredicate(
    state,
    (task) => (showCompletedTasks || !isGanttTaskCompleted(task))
      && (showMarkedAsDeletedTasks || !task.markedAsDeleted),
  );
};

export const isGanttTaskVisibleWithAncestorsOpened = (state: GanttState, task: GanttTask): boolean => {
  let parentId = getGanttTaskParentId(task);
  while (!isGanttTaskRootId(parentId)) {
    const parent = state.tasks[parentId];
    if (!parent || !parent.opened) {
      return false;
    }
    parentId = getGanttTaskParentId(parent);
  }
  return true;
};

export const queryGanttIncludedTasks = (state: GanttState) => queryGanttTasksByPredicate(state, isGanttTaskIncluded);
export const queryGanttAllTasks = (state: GanttState) => queryGanttTasksByPredicate(state, stubTrue);
export const queryGanttStartTask = ({ tasks, tasksByParent, multiMode }: GanttState) => {
  if (multiMode) {
    throw new Error('GanttChartComponent: cannot determine start task in multi mode');
  }

  const taskId = first(tasksByParent[GANTT_TASK_ROOT_ID]);
  return taskId ? tasks[taskId] : void 0;
};
export const queryGanttTask = (state: GanttState, taskId: ID) => state.tasks[taskId];
export const queryGanttTaskByTaskNo = (state: GanttState, taskNo: number): Maybe<GanttTask> => {
  let result;
  for (const key in state.tasks) {
    if (state.tasks[key] && state.tasks[key].taskNumber && state.tasks[key].taskNumber === taskNo) {
      result = state.tasks[key];
      return result;
    }
  }
  return result;
};
export const queryGanttIncludedTasksByPattern = (
  { tasks }: GanttState,
  scheduleId: ID,
  pattern: RegExp,
  excluding?: ID,
) => {
  const taskIds = Object.keys(tasks);
  const strExcluding = isNotNil(excluding) ? String(excluding) : void 0;
  return taskIds
    .filter((taskId) => isGanttTaskIdForSchedule(taskId, scheduleId) && !isGanttTaskTopId(taskId))
    .filter((taskId) => {
      const task = tasks[taskId];
      return taskId !== strExcluding && isGanttTaskIncluded(task)
        && (pattern.test(toString(task.taskNumber)) || pattern.test(task.name));
    })
    .map((taskId) => tasks[taskId]);
};
export const queryGanttHasInvalidTask = ({ tasks }: GanttState) =>
  Object.keys(tasks).some((id) => isEntityInvalid(tasks[id]));
export const queryGanttFirstInvalidTask = (state: GanttState): Maybe<GanttTask> =>
  find<GanttTask>(queryGanttIncludedTasks(state), isEntityInvalid);

export const queryGanttDestinationTaskByNumberForMove = (
  { tasks, tasksByParent }: GanttState,
  sourceTaskId: ID,
  destinationTaskId?: ID,
) => {
  const sourceTask = tasks[sourceTaskId];
  const destinationTask = isNotNil(destinationTaskId) ? tasks[destinationTaskId] : void 0;
  if (destinationTask) {
    // if destination task is opened => we always put source task as first child
    // if destination task is top =>
    //                               if source task is top => we put source task as sibling
    //                               if source task is internal => we put source task as child
    if (isGanttTopTask(destinationTask) ? !isGanttTopTask(sourceTask) : destinationTask.opened) {
      return {
        taskId: getGanttTaskId(destinationTask),
        taskOrder: 0,
      };
    } else if (isGanttStartTask(destinationTask)) {
      // destination task is start task => we move source task under destination one (start task is always first)
      return {
        taskId: destinationTask.parentTaskId,
        taskOrder: 1,
      };
    } else {
      return {
        taskId: destinationTask.parentTaskId,
        taskOrder: tasksByParent[getGanttTaskParentId(destinationTask)].indexOf(getGanttTaskId(destinationTask)),
      };
    }
  } else {
    return {
      taskId: void 0,
      taskOrder: tasksByParent[GANTT_TASK_ROOT_ID].length,
    };
  }
};
export const queryGanttDestinationTaskByNumberForBubble = (
  { tasks, tasksByParent }: GanttState,
  taskId: ID,
) => {
  const task = tasks[taskId];
  const parentTaskId = task ? task.parentTaskId : void 0;
  const parentTask = isNotNil(parentTaskId) ? tasks[parentTaskId] : void 0;
  const parentSiblings = isNotNil(parentTask) ? tasksByParent[getGanttTaskParentId(parentTask)] : void 0;
  return isNotNil(parentTask)
    ? { taskId: parentTask.parentTaskId, taskOrder: parentSiblings!.indexOf(parentTaskId!) + 1 }
    : void 0;
};
export const queryGanttLastChildIndexByNumber = (state: GanttState, taskId?: ID) => {
  const { tasksByParent } = state;
  const children = tasksByParent[ganttTaskIdToParentId(taskId)];
  return children ? children.length : 0;
};
export const queryGanttIsTaskDescendantOf = (
  { tasks }: GanttState,
  taskId: ID,
  possibleAncestorTaskId: ID,
) => {
  let currentTaskId: Maybe<ID> = taskId;
  while (isNotNil(currentTaskId)) {
    if (currentTaskId === possibleAncestorTaskId) {
      return true;
    }

    const currentTask: GanttTask = tasks[currentTaskId];
    currentTaskId = currentTask.parentTaskId;
  }

  return false;
};
export const queryGanttIsTaskHaveCommonLinks = (
  state: GanttState,
  sourceTaskId: ID,
  destinationTaskId: ID,
) => {
  const sourceTaskIds = queryGanttSubtreeTaskIds(state, sourceTaskId);
  const sourceTaskPredecessors = uniq(flatMap(
    sourceTaskIds,
    (id) => map(queryGanttTask(state, id).predecessors, ({ taskId }) => taskId),
  ));

  const destinationTaskIds = queryGanttSubtreeTaskIds(state, destinationTaskId);
  const destinationTaskPredecessors = uniq(flatMap(
    destinationTaskIds,
    (id) => map(queryGanttTask(state, id).predecessors, ({ taskId }) => taskId),
  ));

  return !isEmpty(intersection(sourceTaskIds, destinationTaskPredecessors))
    || !isEmpty(intersection(destinationTaskIds, sourceTaskPredecessors));
};
export const queryGanttIsSameScheduleOrTop = ({ tasks }: GanttState, firstTaskId: ID, secondTaskId: ID) => {
  const firstTask = tasks[firstTaskId];
  const secondTask = tasks[secondTaskId];
  return firstTask.scheduleId === secondTask.scheduleId || isGanttTopTask(firstTask) && isGanttTopTask(secondTask);
};
export const queryGanttIsDragAllowed = (state: GanttState, sourceTaskId: ID, destinationTaskId?: ID) => {
  if (state.multiMode) {
    return isNil(destinationTaskId)
      // we can move to bottom only top level task
      ? isGanttTopTask(queryGanttTask(state, sourceTaskId))
      // otherwise we allow to move only top level tasks or tasks which belongs to the same schedule
      : !queryGanttIsTaskDescendantOf(state, destinationTaskId, sourceTaskId)
      && queryGanttIsSameScheduleOrTop(state, sourceTaskId, destinationTaskId);
  } else {
    // we do not allow to move parent task to its descendant
    return isNil(destinationTaskId) || !queryGanttIsTaskDescendantOf(state, destinationTaskId, sourceTaskId);
  }
};

export const queryGanttSubtreeTaskIds = ({ tasks, tasksByParent }: GanttState, topTaskId: ID) => {
  const subtree: ID[] = [];
  const stack: ID[] = [];
  stack.push(topTaskId);

  while (stack.length) {
    const taskId = stack.pop()!;
    subtree.push(taskId);

    forEachRight(tasksByParent[taskId], (childTaskId) => stack.push(childTaskId));
  }

  return subtree;
};
export const queryGanttAllParentTaskIds = ({ tasksByParent }: GanttState): any[] =>
  Object.keys(tasksByParent).filter(
    (taskId) => taskId !== GANTT_TASK_ROOT_ID && !isEmpty(
      tasksByParent[taskId]));
export const queryGanttNextTaskNumber = ({ tasks }: GanttState) =>
  (max(values(tasks).map((task) => task.taskNumber)) || 0) + 1;
export const queryGanttTaskIndex = ({ tasks, tasksByParent }: GanttState, taskId: ID) => {
  const task = tasks[taskId];
  const parentTaskId = getGanttTaskParentId(task);
  const siblings = tasksByParent[parentTaskId];
  return siblings.indexOf(taskId);
};

export const queryGanttTaskPath = ({ tasks }: GanttState, taskId: ID) => {
  let nextTask: Maybe<GanttTask> = tasks[taskId];
  const taskPath: any[] = [];
  while (nextTask) {
    taskPath.push(getGanttTaskId(nextTask));
    nextTask = nextTask.parentTaskId ? tasks[nextTask.parentTaskId] : void 0;
  }
  taskPath.reverse();
  return taskPath;
};

export const queryTaskCompletionPct = (
  { tasks, tasksByParent }: GanttState,
  parentTaskId: ID,
  resetIfNoChildren: boolean = false,
) => {
  const children = tasksByParent[parentTaskId]
    .map((taskId) => tasks[taskId])
    .filter(isGanttTaskIncluded);
  if (isEmpty(children)) {
    return resetIfNoChildren ? 0 : tasks[parentTaskId].completionPct || 0;
  } else {
    return mean(children.map((task) => task.completionPct || 0)) || 0;
  }
};

export const queryTaskDuration = ({ tasks, tasksByParent }: GanttState, parentTaskId: ID) => {
  const children = tasksByParent[parentTaskId];
  if (isEmpty(children)) {
    return tasks[parentTaskId].duration || 0;
  } else {
    return children
      .map((taskId) => tasks[taskId])
      .reduce((sum, task) => sum + (task.duration || 0), 0);
  }
};

export const queryGanttAncestorTaskIds = ({ tasks, tasksByParent }: GanttState, taskIds: ID[]) => {
  let layer = taskIds;
  let ancestors: ID[] = [];
  while (!isEmpty(layer)) {
    layer = <ID[]>layer.map((layerTaskId) => tasks[layerTaskId].parentTaskId).filter(Boolean);
    ancestors = ancestors.concat(layer);
  }
  return uniq(ancestors);
};

export const queryGanttDescendantTaskIds = ({ tasks, tasksByParent }: GanttState, taskIds: ID[]) => {
  let layer = taskIds;
  let descendants: ID[] = [];
  while (!isEmpty(layer)) {
    layer = <ID[]>flatMap(layer, (layerTaskId) => tasksByParent[layerTaskId]).filter(Boolean);
    descendants = descendants.concat(layer);
  }
  return uniq(descendants);
};

export const queryGanttTotalDuration = (state: GanttState) =>
  calculateCalendarDuration(queryGanttStartDate(state), queryGanttEndDate(state));

export const queryGanttMinDate = (state: GanttState) => state.minDate;
export const queryGanttMultiMode = (state: GanttState) => state.multiMode;
export const queryGanttCurrentDate = ({ currentDate }: GanttState) => currentDate;
export const queryGanttSearchMatch = (state: GanttState, id: ID, searchString: string): boolean => {
  const task = state.tasks[id];
  if (task && searchString && task.taskNumber) {
    const testNumber = padStart(task.taskNumber.toString(), 3, '0').indexOf(searchString) >= 0;
    const testName = !!task.name && task.name.toLowerCase()?.indexOf(searchString) >= 0;
    const testResource = !!task.resource && !!task.resource.name && task.resource.name.toLowerCase()?.indexOf(
      searchString) >= 0;
    return testNumber || testName || testResource;
  }
  return false;
};
